import { DatePipe, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { TagComponent } from '@catch/general-ui';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { MeterGroupModule } from 'primeng/metergroup';
import type { MeterItem } from 'primeng/types/metergroup';
import { CompanyUserRole } from '../../enums/company-user.role.enum';
import { CreditType } from '../../enums/credit-type.enum';
import { CreditsBookingModalHostService } from '../../services/credits-booking-modal-host.service';
import { CreditsLogModalHostService } from '../../services/credits-log-modal-host.service';
import { FontawesomeService } from '../../services/fontawesome.service';
import { hasPermission } from '../../store/auth/auth.reducer';
import {
  selectTotalCredits,
  selectTotalLeftCredits,
  selectTotalLeftCreditsOfType,
  selectTotalReservedCredits,
} from '../../store/credits/credits.reducer';
import { selectSubscription } from '../../store/subscription/subscription.reducer';
import { ButtonComponent } from '../button/button.component';
import {
  buildCreditsMeterItems,
  creditsMeterMax,
  creditsUsedAmount,
} from '../credits-counter/credits-meter.util';

type PopoverCloseEvent = Pick<Event, 'preventDefault' | 'stopPropagation'>;

@Component({
  selector: 'app-credits-counter-expanded',
  imports: [
    DatePipe,
    DecimalPipe,
    ButtonComponent,
    TranslocoPipe,
    FontAwesomeModule,
    TagComponent,
    MeterGroupModule,
    RouterLink,
  ],
  templateUrl: './credits-counter-expanded.component.html',
  styleUrl: './credits-counter-expanded.component.scss',
  host: {
    class: 'ct-card',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreditsCounterExpandedComponent {
  private readonly store = inject(Store);
  private readonly transloco = inject(TranslocoService);
  private readonly creditsBookingModalHost = inject(
    CreditsBookingModalHostService,
  );
  private readonly creditsLogModalHost = inject(CreditsLogModalHostService);
  public readonly fontawesomeService = inject(FontawesomeService);

  /** Shows the payment-management link (e.g. in sidenav popover). */
  readonly showPaymentLink = input(false);
  /** Optional popover dismiss callback when navigating away. */
  readonly closePopover = input<((event?: PopoverCloseEvent) => void) | null>(
    null,
  );

  totalCredits = this.store.selectSignal(selectTotalCredits);
  totalLeftCredits = this.store.selectSignal(selectTotalLeftCredits);
  leftSubscriptionCredits = this.store.selectSignal(
    selectTotalLeftCreditsOfType(CreditType.BASIC),
  );
  leftExtraCredits = this.store.selectSignal(
    selectTotalLeftCreditsOfType(CreditType.ADDITIONAL),
  );
  totalReservedCredits = this.store.selectSignal(selectTotalReservedCredits);
  subscription = this.store.selectSignal(selectSubscription);
  isAdmin = this.store.selectSignal(hasPermission(CompanyUserRole.ADMIN));

  /** ISO timestamp when credits auto top-up was enabled, if active. */
  readonly autoRenewalEnabledAt = computed(
    () =>
      this.subscription()?.subscriptionSummary?.creditsAutoRenewalEnabledAt ??
      null,
  );

  readonly autoRenewalActive = computed(() => !!this.autoRenewalEnabledAt());

  /** Next monthly credit refill date from the current plan, if any. */
  readonly nextCreditsResetDate = computed(
    () =>
      this.subscription()?.subscriptionSummary?.nextCreditsResetDate ?? null,
  );

  /** Credit amount granted on the next monthly refill. */
  readonly nextCreditsResetAmount = computed(
    () => this.subscription()?.subscriptionSummary?.nextCreditsResetAmount ?? 0,
  );

  usedCredits = computed(() =>
    creditsUsedAmount(
      this.totalCredits(),
      this.totalLeftCredits(),
      this.totalReservedCredits(),
    ),
  );

  meterMax = computed(() =>
    creditsMeterMax(
      this.totalLeftCredits(),
      this.totalReservedCredits(),
      this.usedCredits(),
      this.totalCredits(),
    ),
  );

  meterValue = computed((): MeterItem[] =>
    buildCreditsMeterItems({
      leftSubscription: this.leftSubscriptionCredits(),
      leftExtra: this.leftExtraCredits(),
      reserved: this.totalReservedCredits(),
      used: this.usedCredits(),
      translate: (key) => this.transloco.translate(key),
    }),
  );

  /**
   * Opens the credits booking modal for admins.
   * @returns {void}
   */
  openCreditsBookingModal(): void {
    this.creditsBookingModalHost.open({ amount: 0 }).subscribe();
  }

  /**
   * Opens the credit log modal and dismisses an enclosing popover.
   *
   * @param {MouseEvent} event Click event from the log trigger.
   * @returns {void}
   */
  openCreditsLog(event: MouseEvent): void {
    this.closePopover()?.(event);
    this.creditsLogModalHost.open();
  }

  /**
   * Dismisses an enclosing popover when navigating to payment settings.
   * @param {MouseEvent} event Click event from the payment link.
   * @returns {void}
   */
  onPaymentLinkClick(event: MouseEvent): void {
    this.closePopover()?.(event);
  }
}
